import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalLocation } from '@/contexts/LocationContext';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';

const STATUS_META = {
  green: { label: 'Volledig', dot: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  yellow: { label: 'Aandacht', dot: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', bg: 'bg-amber-50' },
  red: { label: 'Krap', dot: 'bg-rose-500', ring: 'ring-rose-200', text: 'text-rose-700', bg: 'bg-rose-50' },
  gray: { label: 'Geen data', dot: 'bg-slate-300', ring: 'ring-slate-200', text: 'text-slate-500', bg: 'bg-slate-50' },
};

function StaffingTile({ loc, onClick }) {
  const meta = STATUS_META[loc.status] || STATUS_META.gray;
  
  let borderAccent = 'border-l-tw1-brand-navy';
  if (loc.status === 'red') borderAccent = 'border-l-tw1-status-danger';
  if (loc.status === 'yellow') borderAccent = 'border-l-tw1-accent-gold';
  if (loc.status === 'gray') borderAccent = 'border-l-slate-300';
  
  return (
    <div
      onClick={onClick}
      data-testid={`locatie-hub-tile-${loc.slug}`}
      className={`group rounded-lg border border-[#e4e9f0] bg-white p-4 shadow-sm hover:shadow-md hover:border-tw1-brand-navy/30 transition-all border-l-[3px] ${borderAccent} block cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${meta.dot} ring-2 ${meta.ring}`} />
            <h4 className="text-base font-bold text-tw1-brand-navy truncate">{loc.name}</h4>
          </div>
          <p className="mt-1 text-xs uppercase tracking-wider text-tw1-text-muted">{loc.type || 'locatie'}</p>
        </div>
        <Badge variant="outline" className={`text-xs ${meta.bg} ${meta.text} border-transparent`}>
          {meta.label}
        </Badge>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-tw1-text-muted">Bezetting</span>
          <span className={`font-semibold tabular-nums ${meta.text}`}>
            {loc.coverage_pct !== null ? `${loc.coverage_pct}%` : '—'}
          </span>
        </div>
        {loc.coverage_pct !== null && (
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${meta.dot}`}
              style={{ width: `${Math.min(loc.coverage_pct, 100)}%` }}
            />
          </div>
        )}
        <div className="flex justify-between text-xs text-tw1-text-muted pt-2 border-t border-slate-100">
          <span>{loc.planned_hours}h / {loc.contract_hours}h</span>
          <span>{loc.employees_active} medew.</span>
        </div>
        {loc.rejected_count > 0 && (
          <div className="mt-2">
            <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-xs">
              {loc.rejected_count} no-show
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LocatiesHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setGlobalLocation } = useGlobalLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/dashboard/full').then(res => {
      if (!cancelled) {
        setData(res.data);
        setLoading(false);
      }
    }).catch(err => {
      console.error('Locaties hub load failed', err);
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 size={20} className="animate-spin mr-2" /> Locaties laden…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="page-locaties">
      <div>
        <h2 className="text-2xl font-bold text-tw1-brand-navy">Locaties</h2>
        <p className="mt-1 text-sm text-tw1-text-muted">
          Selecteer een locatie om de locatiecockpit te openen.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {data.staffing.map((loc) => (
          <StaffingTile 
            key={loc.slug} 
            loc={loc} 
            onClick={() => {
              setGlobalLocation(loc.slug);
              navigate(`/locaties/${loc.slug}`);
            }} 
          />
        ))}
        {data.staffing.length === 0 && (
           <div className="col-span-full rounded-lg border border-[#e4e9f0] bg-white p-8 text-center text-sm text-tw1-text-muted">
             Geen locaties gevonden voor uw profiel.
           </div>
        )}
      </div>
    </div>
  );
}
